/**
 * Route guard for the join-flow wizard.
 *
 * Wraps `/join/:orgId` (and its steps): shows a loading indicator while the
 * onboarding status is in flight, redirects to the dashboard when the org id
 * is missing or the wizard isn't required/already completed, and otherwise
 * renders the wizard. The decision itself is the pure `resolveJoinRouteGuard`
 * in `../guardState.ts`.
 */
import type { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import { useJoinOnboardingStatus } from '../api';
import { resolveJoinRouteGuard } from '../guardState';

interface JoinFlowGuardProps {
  children: ReactNode;
}

export function JoinFlowGuard({ children }: JoinFlowGuardProps) {
  const { orgId } = useParams<{ orgId: string }>();
  const status = useJoinOnboardingStatus(orgId ?? '');

  if (!orgId) {
    return <Navigate to='/organizations' replace />;
  }

  const decision = resolveJoinRouteGuard({
    orgId,
    status: status.data,
    isLoading: status.isLoading,
    isError: status.isError,
  });

  switch (decision.kind) {
    case 'loading':
      return (
        <div className='flex min-h-dvh items-center justify-center bg-[var(--color-bg-primary)]'>
          <LoadingIndicator size='md' />
        </div>
      );
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return children;
  }
}
